<template>
  <div class="w-full max-w-full min-w-0 overflow-x-hidden" dir="rtl">
    <div v-if="user.user_type === 'ma' || user.is_superuser">
      <!-- Header + search -->
      <div class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden mb-4 w-full max-w-full min-w-0">
        <header class="px-3 sm:px-4 py-4 bg-gray-900/50 border-b border-gray-700 min-w-0">
          <h3 class="text-sm font-bold text-gray-100 truncate">جابه‌جایی مالکیت فروش</h3>
          <p class="text-[11px] text-gray-400 mt-1 truncate">
            بارکد محصول را وارد کن تا کل تاریخچه فروشش نمایش داده شود؛ تاریخ و شریک اختیاری هستند
          </p>
        </header>

        <section class="px-3 sm:px-4 py-4 bg-gray-900/30 border-b border-gray-700 min-w-0 overflow-hidden">
          <div class="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 min-w-0">
            <div class="col-span-2 lg:col-span-1 min-w-0">
              <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">بارکد / GTIN محصول</label>
              <input
                  type="text"
                  v-model="gtin"
                  @keyup.enter="search"
                  placeholder="مثلاً 6221234567890"
                  dir="ltr"
                  class="w-full min-w-0 bg-gray-800 border border-gray-600 rounded-xl px-3 py-2 text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
              />
            </div>
            <div class="col-span-1 min-w-0">
              <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">از تاریخ (اختیاری)</label>
              <date placeholder="از تاریخ" v-model="fromDate" />
            </div>
            <div class="col-span-1 min-w-0">
              <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">تا تاریخ (اختیاری)</label>
              <date placeholder="تا تاریخ" v-model="toDate" />
            </div>
            <div class="col-span-2 lg:col-span-1 min-w-0">
              <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5">شریک فروش (اختیاری)</label>
              <partner-select v-model="filterPartner" placeholder="همه شریک‌ها" clearable />
            </div>
            <div class="col-span-2 lg:col-span-1 flex items-end gap-2 min-w-0">
              <button
                  @click="search"
                  :disabled="loading || !gtin.trim()"
                  class="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-xs text-white transition disabled:opacity-50 shrink-0"
              >
                <i class="fas fa-search ml-1"></i> جستجو
              </button>
              <button
                  v-if="fromDate || toDate"
                  @click="fromDate = ''; toDate = ''; search()"
                  class="px-3 py-2 rounded-xl bg-gray-800 border border-gray-600 text-xs text-gray-300 hover:bg-gray-700 transition shrink-0"
              >
                کل تاریخچه
              </button>
            </div>
          </div>
        </section>
      </div>

      <!-- Loading / error -->
      <div v-if="loading" class="p-10 text-center">
        <i class="fas fa-circle-notch fa-spin text-indigo-400 text-2xl"></i>
        <p class="text-gray-400 mt-3 text-sm">در حال جستجو...</p>
      </div>

      <div v-else-if="error" class="p-10 text-center">
        <i class="fas fa-exclamation-triangle text-red-400 text-2xl"></i>
        <p class="text-red-400 mt-3 text-sm">{{ error }}</p>
      </div>

      <div v-else-if="searched && products.length === 0" class="p-10 text-center rounded-2xl bg-gray-900/40 border border-gray-700">
        <i class="fas fa-box-open text-3xl text-gray-600 mb-3 block"></i>
        <p class="text-gray-400 text-sm">نتیجه‌ای با فیلترهای انتخاب‌شده پیدا نشد</p>
      </div>

      <!-- Product picker (only when GTIN matches more than one product) -->
      <div v-else-if="products.length > 1 && !selectedProduct" class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden w-full max-w-full min-w-0">
        <header class="px-3 sm:px-4 py-3 bg-gray-900/50 border-b border-gray-700">
          <h4 class="text-xs font-bold text-gray-200">{{ formatNumber(products.length) }} محصول پیدا شد — یکی رو انتخاب کن</h4>
        </header>
        <div class="p-3 space-y-2 w-full max-w-full min-w-0 overflow-hidden">
          <article
              v-for="product in products"
              :key="product.product_id"
              @click="selectProduct(product)"
              class="flex items-center gap-3 rounded-2xl bg-gray-900/45 border border-gray-700/80 hover:border-indigo-500/60 p-3 shadow-sm cursor-pointer transition min-w-0"
          >
            <div
                class="shrink-0 w-11 h-11 aspect-square rounded-lg overflow-hidden border border-gray-700 bg-gray-800 flex items-center justify-center"
            >
              <img v-if="imgOk(product.image)" :src="img_source(product.image)" class="w-full h-full object-cover" @error="markBroken(product.image)">
              <i v-else class="fas fa-image text-gray-600 text-sm"></i>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs font-bold text-gray-100 truncate">{{ product.product_name }}</p>
              <p class="text-[10px] text-gray-500 mt-0.5" dir="ltr">{{ product.barcode }}</p>
            </div>
            <div class="shrink-0 text-left" dir="ltr">
              <p class="text-[10px] text-gray-500">صاحب فعلی</p>
              <p class="text-xs font-bold text-gray-200">{{ product.current_partner_name || 'بدون شریک' }}</p>
            </div>
            <div class="shrink-0 text-left" dir="ltr">
              <p class="text-[10px] text-gray-500">فروش</p>
              <p class="text-xs font-bold text-gray-200">{{ formatNumber(product.total_quantity) }} عدد</p>
            </div>
            <i class="fas fa-chevron-left text-gray-600 shrink-0"></i>
          </article>
        </div>
      </div>

      <!-- Selected product's transactions -->
      <div v-else-if="selectedProduct" class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden w-full max-w-full min-w-0">
        <header class="px-3 sm:px-4 py-3 bg-gray-900/50 border-b border-gray-700 flex items-center gap-3 min-w-0">
          <button v-if="products.length > 1" @click="selectedProduct = null" class="shrink-0 text-gray-400 hover:text-gray-200 transition text-xs">
            <i class="fas fa-arrow-right"></i>
          </button>
          <div
              class="shrink-0 w-12 h-12 aspect-square rounded-lg overflow-hidden border border-gray-700 bg-gray-800 flex items-center justify-center"
              :class="imgOk(selectedProduct.image) ? 'cursor-zoom-in' : ''"
              @click="zoomImage(selectedProduct.image)"
          >
            <img v-if="imgOk(selectedProduct.image)" :src="img_source(selectedProduct.image)" class="w-full h-full object-cover" @error="markBroken(selectedProduct.image)">
            <i v-else class="fas fa-image text-gray-600 text-sm"></i>
          </div>
          <div class="min-w-0 flex-1">
            <p class="text-xs font-bold text-gray-100 truncate">{{ selectedProduct.product_name }}</p>
            <p class="text-[10px] text-gray-500 mt-0.5" dir="ltr">{{ selectedProduct.barcode }}</p>
          </div>
          <div class="shrink-0 text-left" dir="ltr">
            <p class="text-[10px] text-gray-500">صاحب فعلی محصول</p>
            <p class="text-xs font-bold text-gray-200">{{ selectedProduct.current_partner_name || 'بدون شریک' }}</p>
          </div>
          <div class="shrink-0 text-left" dir="ltr">
            <p class="text-[10px] text-gray-500">جمع فروش</p>
            <p class="text-xs font-bold text-gray-200">{{ formatNumber(selectedProduct.total_quantity) }} عدد</p>
          </div>
        </header>

        <!-- Bulk action bar -->
        <div class="px-3 sm:px-4 py-3 bg-gray-900/30 border-b border-gray-700 min-w-0 overflow-hidden">
          <div class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0">
            <div class="text-[11px] text-gray-400 shrink-0">
              {{ formatNumber(selectedCount) }} تراکنش
              ({{ formatNumber(selectedQuantity) }} عدد) انتخاب شده
              از {{ formatNumber(selectedProduct.transactions.length) }} نتیجه
            </div>
            <div class="flex items-center gap-2 min-w-0 flex-1">
              <button @click="selectAll" class="px-2.5 py-1.5 rounded-lg bg-gray-800 border border-gray-600 text-[11px] text-gray-300 hover:bg-gray-700 transition shrink-0">
                انتخاب همه‌ی نتایج
              </button>
              <button @click="clearSelection" class="px-2.5 py-1.5 rounded-lg bg-gray-800 border border-gray-600 text-[11px] text-gray-300 hover:bg-gray-700 transition shrink-0">
                پاک‌کردن انتخاب
              </button>
              <div class="flex-1 min-w-[140px]">
                <partner-select v-model="targetPartner" placeholder="شریک مقصد" clearable />
              </div>
              <button
                  @click="applyReassignment"
                  :disabled="selectedCount === 0 || applying"
                  class="px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-xs text-white transition disabled:opacity-50 shrink-0"
              >
                <i class="fas fa-circle-notch fa-spin" v-if="applying"></i>
                <i class="fas fa-exchange-alt" v-else></i>
                اعمال به انتخاب‌شده‌ها
              </button>
            </div>
          </div>
        </div>

        <!-- Transactions -->
        <div class="w-full max-w-full min-w-0 overflow-x-auto table-scroll-safe">
          <table class="w-full min-w-[720px] text-sm">
            <thead class="bg-gray-900/70 border-b border-gray-700">
            <tr>
              <th class="px-3 py-3 text-right text-xs font-medium text-gray-400 w-8">
                <input type="checkbox" :checked="allSelected" @change="allSelected ? clearSelection() : selectAll()" class="w-4 h-4 rounded accent-indigo-600">
              </th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">تاریخ</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">کانال</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">سفارش</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">تعداد</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">شریک فعلی</th>
            </tr>
            </thead>
            <tbody class="divide-y divide-gray-700/50">
            <tr
                v-for="tx in selectedProduct.transactions"
                :key="txKey(tx)"
                class="hover:bg-gray-700/30 transition cursor-pointer"
                @click="toggleSelect(tx)"
            >
              <td class="px-3 py-3">
                <input type="checkbox" :checked="isSelected(tx)" @click.stop="toggleSelect(tx)" class="w-4 h-4 rounded accent-indigo-600">
              </td>
              <td class="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{{ tx.date | toJalali('jYYYY/jMM/jDD HH:mm') }}</td>
              <td class="px-4 py-3 text-xs">
                <span :class="sourceBadgeClass(tx.source)" class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border">
                  <i :class="sourceIcon(tx.source)"></i>
                  {{ sourceLabel(tx.source) }}
                </span>
              </td>
              <td class="px-4 py-3 text-xs text-gray-400 whitespace-nowrap text-right" dir="ltr">{{ tx.order_reference }}</td>
              <td class="px-4 py-3 text-xs text-gray-300 whitespace-nowrap text-right" dir="ltr">{{ formatNumber(tx.quantity) }}</td>
              <td class="px-4 py-3 text-xs text-gray-200 font-medium whitespace-nowrap">{{ tx.partner_name || 'بدون شریک' }}</td>
            </tr>

            <tr v-if="selectedProduct.transactions.length === 0">
              <td colspan="6" class="px-4 py-10 text-center text-gray-500 text-xs">فروشی در این بازه ثبت نشده</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-else class="w-full rounded-2xl border border-red-500/30 bg-red-950/40 px-4 py-5 text-right">
      <h3 class="text-sm font-bold text-red-100">دسترسی غیرمجاز</h3>
      <p class="mt-2 text-xs text-red-200">دسترسی برای شما مجاز نیست.</p>
    </div>

    <!-- Image zoom overlay -->
    <div
        v-if="zoomedImage"
        @click="zoomedImage = null"
        class="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6 cursor-zoom-out"
    >
      <img :src="zoomedImage" class="max-w-full max-h-full rounded-2xl shadow-2xl object-contain" @click.stop>
      <button
          @click="zoomedImage = null"
          class="absolute top-4 left-4 sm:top-6 sm:left-6 w-9 h-9 rounded-full bg-gray-900/80 border border-gray-600 text-gray-200 hover:bg-gray-800 transition flex items-center justify-center text-lg"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>
  </div>
</template>

<script>
import getPartnerCalculationsApi from './getPartnerCalculationsApi'
import PartnerSelect from '@/components/selects/PartnerSelect'
import date from "@/components/mcomponents/cleave/Date"

export default {
  name: 'AdminSalesReassignment',

  mixins: [getPartnerCalculationsApi],

  components: { PartnerSelect, date },

  data() {
    return {
      gtin: '',
      filterPartner: null,
      fromDate: '',
      toDate: '',
      loading: false,
      error: null,
      searched: false,
      searchVersion: 0,
      products: [],
      selectedProduct: null,

      selection: new Set(),
      targetPartner: null,
      applying: false,

      zoomedImage: null,
      brokenImages: {},
    }
  },

  watch: {
    gtin(value) {
      if (!value.trim()) this.clearResults()
    },
  },

  computed: {
    selectedCount() {
      return this.selection.size
    },
    selectedQuantity() {
      if (!this.selectedProduct || !this.selection.size) return 0
      return this.selectedProduct.transactions
          .filter(t => this.selection.has(this.txKey(t)))
          .reduce((sum, t) => sum + Number(t.quantity || 0), 0)
    },
    allSelected() {
      return !!this.selectedProduct && this.selectedProduct.transactions.length > 0 &&
          this.selection.size === this.selectedProduct.transactions.length
    },
  },

  methods: {
    getSearchParams() {
      const params = {}
      if (this.gtin.trim()) params.gtin = this.gtin.trim()
      if (this.filterPartner) params.partner_id = this.filterPartner
      if (this.fromDate) params.from = this.fromDate
      if (this.toDate) params.to = this.toDate
      return params
    },

    clearResults() {
      ++this.searchVersion
      this.products = []
      this.selectedProduct = null
      this.selection = new Set()
      this.targetPartner = null
      this.searched = false
      this.loading = false
      this.error = null
    },

    search() {
      if (!this.gtin.trim()) {
        this.clearResults()
        return
      }
      this.loading = true
      this.error = null
      const version = ++this.searchVersion
      const keepProductId = this.selectedProduct ? this.selectedProduct.product_id : null

      this.searchSalesReassignment(
          this.getSearchParams(),
          data => {
            if (version !== this.searchVersion) return
            this.products = data.products || []
            this.searched = true
            this.loading = false

            this.selectedProduct = null
            this.selection = new Set()
            this.targetPartner = null

            if (keepProductId) {
              const same = this.products.find(p => p.product_id === keepProductId)
              if (same) this.selectProduct(same)
            } else if (this.products.length === 1) {
              this.selectProduct(this.products[0])
            }
          },
          err => {
            if (version !== this.searchVersion) return
            this.error = (err && err.response && err.response.data && err.response.data.detail) || 'خطا در جستجو'
            this.loading = false
          }
      )
    },

    selectProduct(product) {
      this.selectedProduct = product
      this.selection = new Set()
      this.targetPartner = null
    },

    txKey(tx) {
      return `${tx.source}:${tx.item_id}`
    },

    isSelected(tx) {
      return this.selection.has(this.txKey(tx))
    },

    toggleSelect(tx) {
      const set = new Set(this.selection)
      const key = this.txKey(tx)
      if (set.has(key)) set.delete(key)
      else set.add(key)
      this.selection = set
    },

    selectAll() {
      this.selection = new Set(this.selectedProduct.transactions.map(t => this.txKey(t)))
    },

    clearSelection() {
      this.selection = new Set()
    },

    applyReassignment() {
      if (!this.selectedProduct || !this.selection.size) return

      const items = this.selectedProduct.transactions
          .filter(t => this.selection.has(this.txKey(t)))
          .map(t => ({ source: t.source, item_id: t.item_id }))

      const partnerLabel = this.targetPartner ? 'شریک انتخاب‌شده' : 'بدون شریک (بی‌صاحب)'

      const confirmed = window.confirm(
          `${items.length} تراکنش (${this.formatNumber(this.selectedQuantity)} عدد) به ${partnerLabel} منتقل بشه؟ این کار روی گزارش‌های مالی شریک‌های قدیم و جدید بلافاصله اثر می‌ذاره.`
      )
      if (!confirmed) return

      this.applying = true
      this.applySalesReassignment(
          { items, partner_id: this.targetPartner || null },
          () => {
            this.applying = false
            this.search()
          },
          () => {
            this.applying = false
            alert('خطا در اعمال تغییرات')
          }
      )
    },

    sourceLabel(source) {
      if (source === 'online') return 'آنلاین'
      if (source === 'in_store') return 'حضوری'
      return 'نامشخص'
    },

    sourceIcon(source) {
      if (source === 'online') return 'fas fa-globe'
      if (source === 'in_store') return 'fas fa-store'
      return 'fas fa-question'
    },

    sourceBadgeClass(source) {
      if (source === 'online') return 'bg-blue-600/15 text-blue-300 border-blue-500/30'
      if (source === 'in_store') return 'bg-purple-600/15 text-purple-300 border-purple-500/30'
      return 'bg-gray-600/15 text-gray-300 border-gray-500/30'
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '۰'
      const number = Number(value)
      if (Number.isNaN(number)) return '۰'
      return new Intl.NumberFormat('fa-IR').format(number)
    },

    imgOk(url) {
      return !!url && !this.brokenImages[url]
    },

    markBroken(url) {
      this.$set(this.brokenImages, url, true)
    },

    zoomImage(url) {
      if (this.imgOk(url)) this.zoomedImage = this.img_source(url)
    },
  },
}
</script>

<style scoped>
table {
  border-collapse: collapse;
}

.table-scroll-safe {
  scrollbar-width: thin;
}
</style>
